import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import { PERFORMANCE_TIME_PERIOD_ALL_TIME } from '@savvy/constants/performance-time-periods';

import type { ClientPortalTimeRange } from './constants';
import { CLIENT_PORTAL_DEFAULT_TIME_RANGE, CLIENT_PORTAL_TIME_RANGES } from './constants';

const EVERY_TIME_RANGE: readonly ClientPortalTimeRange[] = CLIENT_PORTAL_TIME_RANGES.map((range) => range.value);

export function timeRangeWindowStart(range: ClientPortalTimeRange, asOf?: DateTime): DateTime | null {
  const anchor = asOf ?? DateTime.now();
  switch (range) {
    case 'past_month':
      return anchor.minus({ months: 1 });
    case 'past_three_months':
      return anchor.minus({ months: 3 });
    case 'year_to_date':
      return anchor.startOf('year');
    case 'past_year':
      return anchor.minus({ years: 1 });
    case 'all_time':
      return null;
  }
}

export function timeRangeAnchor(asOfDate: string | null | undefined): DateTime {
  if (isNil(asOfDate) || asOfDate === '') {
    return DateTime.now();
  }
  const parsed = DateTime.fromISO(asOfDate);
  return parsed.isValid ? parsed : DateTime.now();
}

export function availableTimeRanges(
  inceptionDate: string | null | undefined,
  asOfDate?: string | null,
): readonly ClientPortalTimeRange[] {
  if (isNil(inceptionDate) || inceptionDate === '') {
    return EVERY_TIME_RANGE;
  }
  const anchor = timeRangeAnchor(asOfDate);
  return EVERY_TIME_RANGE.filter((range) => {
    if (range === PERFORMANCE_TIME_PERIOD_ALL_TIME) {
      return true;
    }
    const windowStart = timeRangeWindowStart(range, anchor)?.toISODate();
    return !isNil(windowStart) && windowStart > inceptionDate;
  });
}

export function latestInceptionDate(...dates: readonly (string | null | undefined)[]): string | null {
  const known = dates.filter((date): date is string => !isNil(date) && date !== '');
  return known.length === 0 ? null : known.reduce((latest, date) => (date > latest ? date : latest));
}

export function resolveTimeRange(
  preferred: ClientPortalTimeRange,
  available: readonly ClientPortalTimeRange[],
): ClientPortalTimeRange {
  if (available.includes(preferred)) {
    return preferred;
  }
  return available.at(-1) ?? CLIENT_PORTAL_DEFAULT_TIME_RANGE;
}
